import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { Label } from '~/client/components/ui/label'
import { toast } from 'sonner'
import { useForm } from '@tanstack/react-form'
import { changePassword } from '~/server/common/supabase/auth'
import { useState } from 'react'

export const Route = createFileRoute('/_app/change-password')({
  component: RouteComponent,
})

type ChangePasswordValues = {
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

function RouteComponent() {
  const navigate = useNavigate()
  const [apiError, setApiError] = useState<string>('')

  const defaultValues: ChangePasswordValues = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  }

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      // Clear any previous API errors
      setApiError('')

      if (value.newPassword !== value.confirmPassword) {
        return
      }

      try {
        await changePassword(value.currentPassword, value.newPassword)

        toast.success('Password changed successfully', {
          position: 'bottom-right',
          richColors: true,
        })

        // Navigate back after successful change
        navigate({ to: '/listings' })
      } catch (err: any) {
        const message = err?.message || 'Failed to change password'
        setApiError(message)
      }
    },
  })

  return (
    <div className="p-6 md:p-10 flex flex-col items-center justify-center">
      <div className="w-full max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="flex flex-col gap-6"
        >
          <h1 className="text-2xl font-bold text-[#18003D]">Change Password</h1>

          <div className="grid gap-3">
            <Label htmlFor="currentPassword">Current Password</Label>
            <form.Field
              name="currentPassword"
              validators={{
                onChange: ({ value }) => (!value ? 'Current password is required' : undefined),
              }}
              children={(field) => (
                <Input
                  id={field.name}
                  type="password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  error={field.state.meta.errors[0] || (apiError ? apiError : undefined)}
                />
              )}
            />
          </div>

          <div className="grid gap-3">
            <Label htmlFor="newPassword">New Password</Label>
            <form.Field
              name="newPassword"
              validators={{
                onChange: ({ value }) => (!value ? 'New password is required' : value.length < 8 ? 'Password must be at least 8 characters' : undefined),
              }}
              children={(field) => (
                <Input id={field.name} type="password" value={field.state.value} onBlur={field.handleBlur} onChange={(e) => field.handleChange(e.target.value)} error={field.state.meta.errors[0]} />
              )}
            />
          </div>

          <div className="grid gap-3">
            <Label htmlFor="confirmPassword">Confirm New Password</Label>
            <form.Field
              name="confirmPassword"
              validators={{
                onChangeListenTo: ['newPassword'],
                onChange: ({ value, fieldApi }) => {
                  if (!value) {
                    return 'Please confirm your new password'
                  }
                  const newPassword = fieldApi.form.getFieldValue('newPassword')
                  if (value !== newPassword) {
                    return 'Passwords do not match'
                  }
                  return undefined
                },
              }}
              children={(field) => (
                <Input id={field.name} type="password" value={field.state.value} onBlur={field.handleBlur} onChange={(e) => field.handleChange(e.target.value)} error={field.state.meta.errors[0]} />
              )}
            />
          </div>

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting] as const}
            children={([canSubmit, isSubmitting]) => (
              <div className="flex gap-3">
                <Button type="submit" className="bg-[#8A3DFF] hover:bg-[#7B2FE6]" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? 'Changing Password...' : 'Change Password'}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate({ to: '/listings' })}>
                  Cancel
                </Button>
              </div>
            )}
          />
        </form>
      </div>
    </div>
  )
}
